import { useState } from "preact/compat"
import { parseISO, format } from "date-fns"
import { Badge, BadgeVariantOptionType } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useAsyncEffect } from "@/utils"
import { copyToClipboard } from "@/utils/copy"
import {
  ConnectMode,
  MailboxInvite,
  MailboxInviteStatus,
  MailboxProvider,
  createMailboxInvite,
  getMailboxInvites,
  provider_display_name,
  revokeMailboxInvite,
  selfConnectMailbox,
} from "@/api/mailbox_invite"
import { UserState } from "@/auth/user"
import { known_mailbox_provider } from "./known_mailbox"
import { useConnectedIntegrations } from "./reconnect_mailboxes"

const STATUS_VARIANT: Record<MailboxInviteStatus, BadgeVariantOptionType> = {
  pending: "pending",
  connected: "won",
  expired: "slate",
  revoked: "slate",
}

interface MailboxConnectPanelProps {
  /** Locks the panel to one provider (the setup flow knows which one). */
  provider?: MailboxProvider
  /**
   * Where to return after connecting from inside the app. Links handed to IT ignore it — they land
   * on the public confirmation page, which is all they can see.
   */
  next?: string
  /** Called after a link connects a mailbox, so the caller can refresh its own list. */
  onConnected?: () => void
  /** Pre-fills the mailbox address. Used when re-connecting a mailbox we already know the name of. */
  defaultEmail?: string
  className?: string
}

/**
 * Creates and manages connect links. The brand names the mailbox here; whoever opens the link can
 * only connect *that* mailbox, so handing the link to IT is safe even though they have no login.
 */
export function MailboxConnectPanel({ provider, next, onConnected, defaultEmail, className }: MailboxConnectPanelProps) {
  // A shareable link works with no MarginWiz session, so only staff mint one. Everyone else can
  // still connect a mailbox they can sign into — the server enforces both halves.
  const user = UserState.use()
  const may_share_links = user?.role === "system_admin"
  const [invites, setInvites] = useState<MailboxInvite[]>([])
  const [email, setEmail] = useState(defaultEmail ?? "")
  const [chosen_provider, setChosenProvider] = useState<MailboxProvider>(provider ?? "microsoft")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<MailboxInvite | null>(null)
  // A mailbox we already sync lives with one provider; that beats both the prop and the dropdown,
  // so a reconnect can never be sent to the other provider's sign-in.
  const known_provider = known_mailbox_provider(email, useConnectedIntegrations())
  const effective_provider = known_provider ?? chosen_provider
  const provider_overridden = known_provider !== null && known_provider !== (provider ?? chosen_provider)

  useAsyncEffect(async () => {
    await refresh()
  }, [])

  async function refresh() {
    if (!may_share_links) return
    const res = await getMailboxInvites()
    if (!res.ok) return
    setInvites(res.value.data)
  }

  async function handle_create(): Promise<MailboxInvite | null> {
    setError(null)
    if (!email.trim()) {
      setError("Enter the mailbox address you want connected.")
      return null
    }
    setBusy(true)
    const res = await createMailboxInvite({ integration_name: effective_provider, email: email.trim() })
    setBusy(false)
    if (!res.ok) {
      setError(res.error.message)
      return null
    }
    setCreated(res.value.data)
    await refresh()
    return res.value.data
  }

  async function handle_connect_myself(mode: ConnectMode) {
    setError(null)
    if (!email.trim()) {
      setError("Enter the mailbox address you want connected.")
      return
    }
    setBusy(true)
    const res = await selfConnectMailbox({
      integration_name: effective_provider,
      email: email.trim(),
      mode,
      next: next ?? window.location.pathname,
    })
    if (!res.ok) {
      setBusy(false)
      setError(res.error.message)
      return
    }
    window.location.href = res.value.data
  }

  async function handle_revoke(id: string) {
    await revokeMailboxInvite(id)
    if (created?.id === id) setCreated(null)
    await refresh()
    onConnected?.()
  }

  return (
    <div class={className}>
      <div class="space-y-3">
        <div class="grid gap-3 sm:grid-cols-[1fr_auto]">
          <div class="space-y-1">
            <Label htmlFor="mailbox-invite-email">Mailbox to connect</Label>
            <Input
              id="mailbox-invite-email"
              type="email"
              placeholder="deductions@yourcompany.com"
              value={email}
              onInput={(e: Event) => {
                setCreated(null)
                setEmail((e.currentTarget as HTMLInputElement).value)
              }}
            />
          </div>
          {!provider && (
            <div class="space-y-1">
              <Label htmlFor="mailbox-invite-provider">Provider</Label>
              <Select
                value={effective_provider}
                disabled={known_provider !== null}
                onValueChange={(v: string) => {
                  setCreated(null)
                  setChosenProvider(v as MailboxProvider)
                }}>
                <SelectTrigger id="mailbox-invite-provider" class="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="microsoft">Microsoft 365</SelectItem>
                  <SelectItem value="gmail">Google Workspace</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <p class="text-xs text-gray-500">
          We'll only ever sync this mailbox. If someone signs in with a different account, the
          connection is refused unless that account provably has access to this mailbox.
        </p>
        {provider_overridden && (
          <p class="text-xs text-gray-700">
            {email.trim()} is already connected as a {provider_display_name(effective_provider)} mailbox, so you'll
            sign in with {effective_provider === "gmail" ? "Google" : "Microsoft"}.
          </p>
        )}
        {!may_share_links && (
          <p class="text-xs text-gray-500">
            Sign in as this mailbox to connect it{effective_provider === "microsoft" ? ", or as an admin who has Full Access to it" : ""}. If
            someone else manages it, ask your MarginWiz contact for a link they can use.
          </p>
        )}

        {error && <p class="text-sm text-red-600">{error}</p>}

        <div class="flex flex-wrap gap-2">
          <Button type="button" disabled={busy} onClick={() => handle_connect_myself("direct")}>
            Connect it now
          </Button>
          {/*
            Shared mailboxes usually can't sign in at all, so hinting one would send the admin to an
            account Microsoft won't accept. Same verification either way — the callback still proves
            the account can read this mailbox before writing anything. Google has no equivalent.
          */}
          {effective_provider === "microsoft" && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => handle_connect_myself("delegated")}>
              It's a shared mailbox
            </Button>
          )}
          {may_share_links && (
            <Button type="button" variant="outline" disabled={busy} onClick={handle_create}>
              Get a link for IT
            </Button>
          )}
        </div>

        {created && <InviteLinkBox invite={created} />}
      </div>

      {invites.length > 0 && (
        <div class="mt-6 space-y-2">
          <h4 class="text-sm font-medium text-gray-900">Connect links</h4>
          <div class="divide-y divide-gray-100 border border-gray-200 rounded-lg">
            {invites.map(invite => (
              <InviteRow key={invite.id} invite={invite} onRevoke={handle_revoke} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function InviteLinkBox({ invite }: { invite: MailboxInvite }) {
  return (
    <div class="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
      <p class="text-sm text-gray-700">
        Send this link to whoever manages {invite.email}. It works until{" "}
        {format(parseISO(invite.expires_at), "MMM d, yyyy")} and needs no MarginWiz account.
      </p>
      <div class="flex items-center gap-2">
        <code class="text-xs bg-white border border-gray-200 rounded px-2 py-1 flex-1 min-w-0 truncate">{invite.url}</code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => copyToClipboard(invite.url, "Connect link copied to clipboard")}>
          Copy
        </Button>
      </div>
    </div>
  )
}

interface InviteRowProps {
  invite: MailboxInvite
  onRevoke: (id: string) => void
}

function InviteRow({ invite, onRevoke }: InviteRowProps) {
  const revocable = invite.status === "pending" || invite.status === "connected"
  return (
    <div class="p-3 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-900 truncate min-w-0">{invite.email}</span>
          <Badge variant={STATUS_VARIANT[invite.status]}>{invite.status}</Badge>
        </div>
        <p class="text-xs text-gray-500 mt-0.5">
          {provider_display_name(invite.integration_name)} ·{" "}
          {invite.status === "connected" && invite.connected_by_account
            ? `authorized by ${invite.connected_by_account}`
            : invite.tenant_id
              ? `admin consent granted (tenant ${invite.tenant_id}), waiting for the Exchange mailbox scope`
              : `expires ${format(parseISO(invite.expires_at), "MMM d, yyyy")}`}
        </p>
        {invite.last_error && invite.status !== "connected" && (
          <p class="text-xs text-red-600 mt-1">{invite.last_error}</p>
        )}
      </div>
      <div class="flex items-center gap-2 shrink-0">
        {invite.status !== "revoked" && invite.status !== "expired" && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copyToClipboard(invite.url, "Connect link copied to clipboard")}>
            Copy link
          </Button>
        )}
        {revocable && (
          <Button type="button" size="sm" variant="ghost" onClick={() => onRevoke(invite.id)}>
            Revoke
          </Button>
        )}
      </div>
    </div>
  )
}
